import { useGetAddonsDataQuery } from '@/src/api/rtkQuery/addonsApi'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import { ContactDetailsFormProps } from '@/src/components/common/forms/contactDetailsForm'
import { PersonalDetailsFormProps } from '@/src/components/common/forms/personalDetailsForm'
import useClevertapForItinerary from '@/src/hooks/analytics/clevertap/itinerary'
import { getActiveStep, getCompletedSteps, setActiveStep } from '@/src/redux/slices/itinerarySlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import { DeviceType } from '@/src/types/deviceType'
import {
    Customer,
    DocumentDetails,
    DocumentType,
    ItineraryStepNumberType,
    NonDocumentField,
    TaxDetails,
    Traveler
} from '@/src/types/itinerary'
import { htmlSanitizer } from '@/src/utils/browser'
import { isoStringToDateObject } from '@/src/utils/itinerary/dateTransformer'
import { saveTravelerDataLocal } from '@/src/utils/itinerary/travelerDataLocalStoreManager'
import * as TravelerDocumentUtils from '@/src/utils/itinerary/travelerDocumentToInputsSegregater'
import { useCallback, useState } from 'react'
import useSmbMandatoryCheck from '../../addons/useSmbMandatoryCheck'
import useGetItineraryId from '../../useGetItineraryId'
import useItineraryUtils from '../../useItineraryUtils'
import { useContactDetails } from '../useContactDetails'
import { useSavedTraveler } from '../useSavedTraveler'
import { useTaxDetails } from '../useTaxDetails'
import { useTravelerState } from '../useTravelerState'
import { useUpdateItineraryAndPrepayment } from '../useUpdateitinearyAndPrepayment'
import { useValidationHelpers } from '../useValidationHelpers'
import { useValidationManager } from '../useValidationManager'

const useAddTravelersStepStateManager = ({
    deviceType,
    stepNumber
}: {
    deviceType: DeviceType
    stepNumber: ItineraryStepNumberType
}) => {
    const dispatch = useAppDispatch()
    const { triggerItineraryEvent } = useClevertapForItinerary()
    const itineraryId = useGetItineraryId()
    const activeStep = useAppSelector(getActiveStep)
    const completedSteps = useAppSelector(getCompletedSteps)
    const { getIsStepCompleted } = useItineraryUtils()
    const isStepActive = deviceType === 'MOBILE' ? true : activeStep === stepNumber
    const { passengers, customer, mandatoryFields, isItineraryLoading, isItineraryError, arrivalDate, departureDate } =
        useGetItineraryDataQuery(
            {
                itineraryId
            },
            {
                selectFromResult: ({ data, isError, isLoading }) => ({
                    passengers: data?.passengers || [],
                    customer: (data?.customer || {}) as Customer,
                    mandatoryFields: (data?.meta?.mandatoryFields || []) as (DocumentType | NonDocumentField)[],
                    isItineraryLoading: isLoading || false,
                    isItineraryError: isError || false,
                    arrivalDate: data?.searchCriteria.arrivalDate
                        ? isoStringToDateObject(data?.searchCriteria.arrivalDate)
                        : null,
                    departureDate: data?.searchCriteria.departDate
                        ? isoStringToDateObject(data?.searchCriteria.departDate)
                        : null
                })
            }
        )
    const { isMealMandatory, isSeatMandatory } = useSmbMandatoryCheck()
    const { data: addonsData } = useGetAddonsDataQuery({ itineraryId })
    const showMandatorySmbPopUp = (isMealMandatory || isSeatMandatory) && !addonsData?.isAncillaryUpdated

    const documentTypes: DocumentType[] = ['NATIONALID', 'IQAMA', 'PASSPORT']
    const requiredDocuments = mandatoryFields
        .filter(field => documentTypes.includes(field as DocumentType))
        .sort((a, b) => {
            const aIndex = documentTypes.indexOf(a as DocumentType)
            const bIndex = documentTypes.indexOf(b as DocumentType)
            return aIndex - bIndex
        }) as DocumentType[]

    const isNationalityRequired = mandatoryFields.includes('NATIONALITY')
    const isDateOfBirthRequired = mandatoryFields.includes('DOB')
    const isNationalityRequiredToRenderInUserPersonalDetailsSection =
        isNationalityRequired && requiredDocuments.length === 0
    const isBirthCountryRequired = mandatoryFields.includes('BIRTH_COUNTRY')

    // Use modular hooks
    const {
        travelers,
        setTravelers,
        onChangeTravelerDetails,
        onChangeDocumentField,
        travelersIndexToPassengerTypeAndSerialNumberMap
    } = useTravelerState({
        mandatoryFields,
        passengers,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isDateOfBirthRequired,
        requiredDocuments,
        isBirthCountryRequired
    })

    const { contactDetails, onChangeContactDetails } = useContactDetails(customer)

    const { taxDetails, onChangeTaxDetails } = useTaxDetails()

    const { travelerValidation, contactValidation, taxValidation } = useValidationManager({
        deviceType,
        mandatoryFields,
        passengers,
        requiredDocuments,
        isDateOfBirthRequired,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        arrivalDate,
        departureDate,
        isBirthCountryRequired
    })

    const { validateAllForms, validateTraveler } = useValidationHelpers({
        travelers,
        travelerValidation,
        contactValidation,
        taxValidation,
        contactDetails,
        taxDetails,
        isBirthCountryRequired,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isDateOfBirthRequired
    })

    const { onSavedTravelerSelect } = useSavedTraveler({
        travelers,
        setTravelers,
        validateTraveler,
        contactValidation,
        contactDetails,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isDateOfBirthRequired,
        isBirthCountryRequired
    })

    // onChangeTravelerDetails with validation
    const handleChangeTravelerDetails: PersonalDetailsFormProps['onChangeTravelerDetails'] = useCallback(
        (idx, field, value) => {
            const sanitizedValue = htmlSanitizer(value)
            onChangeTravelerDetails(idx, field, sanitizedValue)
            const fieldKey = `traveler_${idx}_${field}`
            const current = travelerValidation.validationState?.[fieldKey]
            if (current?.isError) {
                travelerValidation.clearFieldError(fieldKey)
            }
        },
        [onChangeTravelerDetails, travelerValidation]
    )

    const handleBlurTravelerDetails = useCallback(
        (idx: number, field: keyof Traveler, inputValue: any) => {
            const fieldKey = `traveler_${idx}_${field}`
            const value = htmlSanitizer(inputValue) ?? travelers[idx]?.[field]
            travelerValidation.validateField(fieldKey, value)
        },
        [travelerValidation, travelers]
    )

    // onChangeDocumentField with validation
    const handleChangeDocumentField = useCallback(
        (travelerIndex: number, documentType: DocumentType, field: string, value: any) => {
            const sanitizedValue = htmlSanitizer(value)
            onChangeDocumentField(travelerIndex, documentType, field, sanitizedValue)
            const fieldKey = `traveler_${travelerIndex}_${documentType}_${field}`
            const current = travelerValidation.validationState?.[fieldKey]
            if (current?.isError) {
                travelerValidation.clearFieldError(fieldKey)
            }
        },
        [onChangeDocumentField, travelerValidation]
    )

    const handleBlurDocumentField = useCallback(
        (travelerIndex: number, documentType: DocumentType, field: string, inputValue: any) => {
            // Only validate the specific field that was changed
            const documentInputs = TravelerDocumentUtils.travelerDocumentToInputsSegregate(documentType)
            const fieldKey = `traveler_${travelerIndex}_${documentType}_${field}`
            const value =
                htmlSanitizer(inputValue) ??
                travelers[travelerIndex]?.documentDetails?.[documentType]?.[field as keyof DocumentDetails]

            // Only validate if this field is required for this document type
            if (documentInputs[field as keyof DocumentDetails]) {
                travelerValidation.validateField(fieldKey, value)
            }
        },
        [travelerValidation, travelers]
    )

    // onChangeContactDetails with validation
    const handleChangeContactDetails: ContactDetailsFormProps['onChange'] = useCallback(
        (key, value) => {
            const sanitizedValue = htmlSanitizer(value)
            onChangeContactDetails(key, sanitizedValue)
            const current = contactValidation.validationState?.[key]
            if (current?.isError) {
                contactValidation.clearFieldError(key)
            }
        },
        [onChangeContactDetails, contactValidation]
    )

    const handleBlurContactDetails = useCallback(
        (key: keyof Customer, inputValue: any) => {
            const value = htmlSanitizer(inputValue) ?? contactDetails[key]
            if (key === 'mobile') {
                contactValidation.validateField(key, {
                    mobile: value,
                    mobileCountryCode: contactDetails.mobileCountryCode
                })
            } else if (key === 'mobileCountryCode') {
                contactValidation.validateField(key, value)
                contactValidation.validateField('mobile', {
                    mobile: contactDetails.mobile,
                    mobileCountryCode: value
                })
            } else {
                contactValidation.validateField(key, value)
            }
        },
        [contactValidation, contactDetails]
    )

    // onChangeTaxDetails with validation
    const handleChangeTaxDetails = useCallback(
        (key: keyof TaxDetails, value: string | boolean) => {
            const sanitizedValue = htmlSanitizer(value)
            onChangeTaxDetails(key, sanitizedValue)
            const current = taxValidation.validationState?.[key]
            if (current?.isError) {
                taxValidation.clearFieldError(key)
            }
        },
        [onChangeTaxDetails, taxValidation]
    )

    const handleBlurTaxDetails = useCallback(
        (key: keyof TaxDetails, inputValue: any) => {
            const value = htmlSanitizer(inputValue) ?? taxDetails[key]
            taxValidation.validateField(key, value)
        },
        [taxValidation, taxDetails]
    )

    const [activeTravelerIndexes, setActiveTravelerIndexes] = useState<number[]>([0]) //it's an array because we can expand multiple travelers details at a time for scenario

    const scrollToTraveler = (idx: number) => {
        const upperSiblingElementIndex = idx - 1
        setTimeout(() => {
            const targetElement = document.getElementById(`traveler-${upperSiblingElementIndex}`)
            if (targetElement) {
                targetElement.scrollIntoView({ block: 'start', behavior: 'smooth' })
            }
        }, 500)
    }

    const onSaveTraveler = (idx: number) => {
        const isTravelerValid = validateTraveler(idx)
        const contactIsValid =
            idx === 0
                ? contactValidation.validateAll({
                      email: contactDetails.email,
                      mobile: {
                          mobile: contactDetails.mobile,
                          mobileCountryCode: contactDetails.mobileCountryCode
                      },
                      mobileCountryCode: contactDetails.mobileCountryCode
                  })
                : true

        if (isTravelerValid && contactIsValid) {
            setTravelers(prev => {
                const newTravelers = [...prev]
                newTravelers[idx].showSuccess = true
                newTravelers[idx].showError = false
                newTravelers[idx].showNameAtAccordionSummary = true
                return newTravelers
            })
            setActiveTravelerIndexes([idx + 1])
            scrollToTraveler(idx + 1)
            //clevertap event
            triggerItineraryEvent('Air_UI_Action', {
                action_name: 'add_traveller',
                action_type: 'BUTTON'
            })
        } else {
            setTravelers(prev => {
                const newTravelers = [...prev]
                newTravelers[idx].showError = true
                newTravelers[idx].showSuccess = false
                return newTravelers
            })
        }
    }

    const handleExpandTraveler = (idx: number, expanded: boolean) => {
        if (idx < 0 || idx >= travelers.length) return
        setActiveTravelerIndexes(prev => (prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]))

        if (!expanded) {
            onSaveTraveler(idx)
        } else {
            // scrollToTraveler(idx)
        }
    }

    const onStepClick = useCallback(() => {
        if (!isStepActive && getIsStepCompleted(stepNumber)) {
            dispatch(setActiveStep(stepNumber))
        }
    }, [dispatch, isStepActive, stepNumber])

    const {
        handleUpdateItineraryAndPrepayment,
        handlePrePayment,
        isUpdateItineraryLoading,
        isUpdateItineraryError,
        isUpdateItinerarySuccess,
        isPrePaymentLoading,
        isPrePaymentError,
        isPrePaymentSuccess,
        prePaymentErrorType,
        showCouponExpiry,
        setShowCouponExpiry
    } = useUpdateItineraryAndPrepayment({
        travelers,
        itineraryId,
        contactDetails,
        isDateOfBirthRequired,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isBirthCountryRequired,
        taxDetails,
        deviceType
    })

    const handleContinue = async () => {
        const isAllFormsValid = validateAllForms()

        if (!isAllFormsValid) {
            for (let idx = 0; idx < travelers.length; idx++) {
                const isContactValid = idx === 0 ? contactValidation.isValid : true
                if (validateTraveler(idx) && isContactValid) {
                    setTravelers(prev => {
                        const newTravelers = [...prev]
                        newTravelers[idx].showSuccess = true
                        newTravelers[idx].showError = false
                        newTravelers[idx].showNameAtAccordionSummary = true
                        return newTravelers
                    })
                } else {
                    setTravelers(prev => {
                        const newTravelers = [...prev]
                        newTravelers[idx].showError = true
                        newTravelers[idx].showSuccess = false
                        return newTravelers
                    })
                    setActiveTravelerIndexes([idx])
                    scrollToTraveler(idx)
                    return
                }
            }
            return
        }

        saveTravelerDataLocal({
            // contactDetails,
            itineraryId,
            // taxDetails,
            travelers,
            travelersIndexToPassengerTypeAndSerialNumberMap
        })
        //ready to update itinerary
        await handleUpdateItineraryAndPrepayment()
    }

    return {
        isStepActive,
        travelers,
        contactDetails,
        travelersIndexToPassengerTypeAndSerialNumberMap,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isBirthCountryRequired,
        isDateOfBirthRequired,
        taxDetails,
        onChangeTaxDetails: handleChangeTaxDetails,
        onChangeContactDetails: handleChangeContactDetails,
        onChangeTravelerDetails: handleChangeTravelerDetails,
        onChangeDocumentField: handleChangeDocumentField,
        handleBlurTravelerDetails,
        handleBlurDocumentField,
        handleBlurContactDetails,
        handleBlurTaxDetails,
        // Validation states
        travelerValidation,
        contactValidation,
        taxValidation,
        validateAllForms,
        //  traveler validation functions

        validateTraveler,
        onSavedTravelerSelect,
        activeTravelerIndexes,
        handleExpandTraveler,
        setActiveTravelerIndexes,
        onStepClick,
        onSaveTraveler,
        handleContinue,
        isUpdateItineraryLoading,
        isUpdateItineraryError,
        isUpdateItinerarySuccess,
        isPrePaymentLoading,
        isPrePaymentError,
        isPrePaymentSuccess,
        prePaymentErrorType,
        showCouponExpiry,
        setShowCouponExpiry,
        triggerPrepaymentOnly: handlePrePayment,

        //Mandatory SMB Popup
        showMandatorySmbPopUp
    }
}

export default useAddTravelersStepStateManager
